// ═══════════════════════════════════════════════════════════════
// frontend/src/components/Layout.tsx — Main layout with sidebar
// ═══════════════════════════════════════════════════════════════

import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

const navItems = [
  { to: "/", label: "Dashboard", icon: "📊" },
  { to: "/partners", label: "Partners", icon: "👥" },
  { to: "/products", label: "Products", icon: "📦" },
  { to: "/crm", label: "CRM", icon: "🎯" },
  { to: "/orders", label: "Sales Orders", icon: "🛒" },
  { to: "/invoices", label: "Invoices", icon: "💰" },
  { to: "/deliveries", label: "Deliveries", icon: "🚚" },
  { to: "/inventory", label: "Inventory", icon: "📋" },
  { to: "/purchases", label: "Purchases", icon: "📥" },
  { to: "/employees", label: "Employees", icon: "👤" },
  { to: "/manufacturing", label: "Manufacturing", icon: "🏭" },
  { to: "/projects", label: "Projects", icon: "📋" },
  { to: "/tasks", label: "Tasks", icon: "✅" },
  { to: "/expenses", label: "Expenses", icon: "💳" },
  { to: "/store", label: "Storefront", icon: "🛍️" },
  { to: "/pos", label: "POS Terminal", icon: "🖥️" },
  { to: "/helpdesk", label: "Helpdesk", icon: "🎫" },
  { to: "/campaigns", label: "Campaigns", icon: "📧" },
  { to: "/subscriptions", label: "Subscriptions", icon: "🔄" },
];

export default function Layout() {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout } = useAuth();

  return (
    <div className="flex h-screen">
      {/* Sidebar */}
      <aside className={`bg-gray-900 text-white transition-all duration-200 ${collapsed ? "w-16" : "w-56"} flex flex-col`}>
        <div className="p-4 border-b border-gray-700">
          <h1 className={`font-bold ${collapsed ? "text-sm text-center" : "text-lg"}`}>
            {collapsed ? "CE" : "CloudERP"}
          </h1>
        </div>
        <nav className="flex-1 p-2 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  isActive ? "bg-blue-600 text-white" : "text-gray-300 hover:bg-gray-800"
                }`
              }
            >
              <span className="text-lg">{item.icon}</span>
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-gray-700">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="text-gray-400 hover:text-white text-sm w-full text-left"
          >
            {collapsed ? "→" : "Collapse"}
          </button>
        </div>
      </aside>

      {/* Main area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="bg-white border-b px-6 py-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-700">CloudERP</h2>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">{user?.name ?? "Dev User"}</span>
            <button onClick={logout} className="text-sm text-red-600 hover:text-red-800">
              Logout
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-6 bg-gray-50">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
